import React from 'react';
import { Filter, User, Calendar, Mail, Phone, FileText } from 'lucide-react';
import DisputePanel from '../../components/DisputePanel';

const TIER_BADGE = {
  'Prime': { color: '#4ade80', bg: 'rgba(74, 222, 128, 0.15)' },
  'Near-Prime': { color: '#ffd76a', bg: 'rgba(255, 215, 106, 0.15)' },
  'Subprime': { color: '#fb923c', bg: 'rgba(251, 146, 60, 0.15)' },
  'Deep Subprime': { color: '#f87171', bg: 'rgba(248, 113, 113, 0.15)' },
};

const FundingQualificationCard = ({ item, onDownload, formatDate, token }) => {
  const fe = item.fundingEligibility || {};
  const m = item.metrics || {};
  const scores = m.creditScores || {};
  const badge = TIER_BADGE[fe.tier] || TIER_BADGE['Near-Prime'];
  return (
    <div className="admin-card" data-testid="fq-admin-card">
      <div className="admin-card-header">
        <div className="admin-card-name">
          <User size={20} />
          <span>{item.firstName} {item.lastName}</span>
        </div>
        <div className="admin-card-date">
          <Calendar size={16} />
          <span>{formatDate(item.uploadedAt)}</span>
        </div>
      </div>

      <div
        className="admin-service-badge"
        style={{ background: badge.bg, color: badge.color, borderColor: badge.color }}
      >
        {fe.tier || '—'} · Score {scores.average ?? '—'} · Readiness {fe.readinessScore ?? '—'}/100
      </div>

      <div className="admin-card-contacts">
        <div className="admin-contact-item">
          <Mail size={16} />
          <a href={`mailto:${item.email}`}>{item.email}</a>
        </div>
        {item.phone && (
          <div className="admin-contact-item">
            <Phone size={16} />
            <a href={`tel:${item.phone}`}>{item.phone}</a>
          </div>
        )}
      </div>

      <div className="admin-card-details">
        <div className="admin-detail-item">
          <span className="detail-label">Personal Funding:</span>
          <span className="detail-value">
            ${(fe.estimatedPersonalFundingLow ?? 0).toLocaleString()}–${(fe.estimatedPersonalFundingHigh ?? 0).toLocaleString()}
          </span>
        </div>
        <div className="admin-detail-item">
          <span className="detail-label">Business Funding:</span>
          <span className="detail-value">
            ${(fe.estimatedBusinessFundingLow ?? 0).toLocaleString()}–${(fe.estimatedBusinessFundingHigh ?? 0).toLocaleString()}
          </span>
        </div>
        <div className="admin-detail-item">
          <span className="detail-label">Utilization:</span>
          <span className="detail-value">{m.utilizationPct != null ? `${m.utilizationPct}%` : '—'}</span>
        </div>
        <div className="admin-detail-item">
          <span className="detail-label">Derogs:</span>
          <span className="detail-value">{m.derogatoryMarks ?? '—'}</span>
        </div>
      </div>

      {fe.oneLineVerdict && (
        <div className="admin-card-message">
          <p className="message-label">Verdict:</p>
          <p className="message-text">{fe.oneLineVerdict}</p>
        </div>
      )}

      {Array.isArray(item.preQualifiedOffers) && item.preQualifiedOffers.length > 0 && (
        <div className="admin-offers" data-testid={`admin-offers-${item.id}`}>
          <p className="admin-offers-label">Matched Lender Offers (admin-only):</p>
          <div className="admin-offers-list">
            {item.preQualifiedOffers.map((offer) => (
              <a
                key={offer.id}
                href={offer.affiliateUrl}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="admin-offer-link"
                data-testid={`admin-offer-${item.id}-${offer.id}`}
              >
                {offer.name} → Send Offer
              </a>
            ))}
          </div>
        </div>
      )}

      <DisputePanel item={item} token={token} />

      <button
        type="button"
        className="btn-primary"
        style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', fontSize: 13 }}
        onClick={() => onDownload(item.id)}
        data-testid={`fq-download-${item.id}`}
      >
        <FileText size={16} /> Download Credit Report PDF
      </button>
    </div>
  );
};

const FundingQualifierPanel = ({ items, loading, tierFilter, setTierFilter, onDownload, formatDate }) => {
  if (loading) {
    return (
      <section className="admin-applications">
        <div className="admin-empty"><p className="body-large">Loading funding qualifier submissions…</p></div>
      </section>
    );
  }
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('adminToken') : '';
  return (
    <>
      <section className="admin-controls">
        <div className="admin-controls-content">
          <div className="admin-filters">
            <div className="admin-filter-group">
              <Filter size={20} />
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="admin-select"
                data-testid="fq-tier-filter"
              >
                <option value="">All Tiers</option>
                <option value="Prime">Prime (720+)</option>
                <option value="Near-Prime">Near-Prime (660-719)</option>
                <option value="Subprime">Subprime (580-659)</option>
                <option value="Deep Subprime">Deep Subprime (&lt;580)</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <section className="admin-applications">
        {items.length === 0 ? (
          <div className="admin-empty">
            <p className="body-large">No funding qualifier submissions yet{tierFilter ? ` for tier "${tierFilter}"` : ''}.</p>
          </div>
        ) : (
          <div className="admin-grid">
            {items.map((it) => (
              <FundingQualificationCard
                key={it.id}
                item={it}
                onDownload={onDownload}
                formatDate={formatDate}
                token={token}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default FundingQualifierPanel;
